import { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GET_VENDOR_NOTIFICATIONS,
  VENDOR_NOTIFICATION_RESPONSE,
  VENDOR_SWIPE_LEFT,
  IGNORE_NOTIFICATION,
} from 'api/graphql/notifications';

/**
 * Vendor notifications + the mutations to respond (accept / reject / ignore).
 *
 * Always fetches fresh (network-only) and refetches whenever the tab regains
 * focus. We do NOT filter by type here — the dashboard shows whatever the API
 * returns. Responses are logged for debugging.
 */
export function useVendorNotifications() {
  const { data, loading, error, refetch } = useQuery(GET_VENDOR_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const notifications = data?.vendorNotifications ?? [];

  // Refetch on tab focus so the dashboard stays current.
  useEffect(() => {
    const onFocus = () => {
      refetch?.();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch]);

  // Debug logging — confirm what the API returns.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      '[notifications] vendorNotifications count:',
      data?.vendorNotifications?.length ?? 0,
      data?.vendorNotifications,
    );
  }, [data]);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[notifications] error:', error.message, error);
    }
  }, [error]);

  const [respond] = useMutation(VENDOR_NOTIFICATION_RESPONSE);
  const [swipeLeft] = useMutation(VENDOR_SWIPE_LEFT);
  const [ignore] = useMutation(IGNORE_NOTIFICATION);

  const accept = async ({ notificationId, vendorPrice, vendorCatalogId, quantity, productId }) => {
    const result = await respond({
      variables: {
        notificationId,
        vendorPrice,
        vendorCatalogId: vendorCatalogId || '',
        quantity,
        productId,
      },
    });
    return result.data?.vendorNotificationResponse ?? null;
  };

  const reject = async ({ productId, rejectionReason, notificationId }) => {
    const result = await swipeLeft({ variables: { productId, rejectionReason, notificationId } });
    return result.data?.vendorSwipeLeft ?? null;
  };

  const ignoreOne = async (notificationId) => {
    const result = await ignore({ variables: { notificationId } });
    return result.data?.ignoreNotification ?? null;
  };

  return { notifications, loading, error, refetch, accept, reject, ignoreOne };
}
